import { format, isValid, parseISO } from 'date-fns';
import type { RenewalContract, RenewalQuote } from '~/services/modules/renewal';
import { BRAZIL_UFS, validateBrazilAddress, type BrazilAddressFields } from '~/utils/brazilAddress';

export { BRAZIL_UFS };

const EMPTY = '—';
const digitsOf = (value: string | null | undefined) => (value ?? '').replace(/\D/g, '');

export function formatRenewalCpf(value: string | null | undefined): string {
  const digits = digitsOf(value);
  return digits.length === 11 ? digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : EMPTY;
}

export function formatRenewalPhone(value: string | null | undefined): string {
  const digits = digitsOf(value);
  if (digits.length === 11) return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return EMPTY;
}

export function formatRenewalZipcode(value: string | null | undefined): string {
  const digits = digitsOf(value);
  return digits.length === 8 ? digits.replace(/^(\d{5})(\d{3})$/, '$1-$2') : EMPTY;
}

export function formatRenewalBirthdate(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const date = parseISO(value);
  return isValid(date) ? format(date, 'dd/MM/yyyy') : EMPTY;
}

export function formatRenewalCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return EMPTY;
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export type RenewalAddressFields = BrazilAddressFields;

/** `Gavião Peixoto, 156 Icaraí - Niterói/RJ - casa` (Figma 2086:8515). */
export function formatRenewalAddressLine(address: Partial<RenewalAddressFields>): string {
  const head = [address.street, address.number].filter(Boolean).join(', ');
  const place = [address.city, address.state].filter(Boolean).join('/');
  const parts = [[head, address.neighborhood].filter(Boolean).join(' '), place, address.additional_details];
  const line = parts.filter((part) => part && String(part).trim()).join(' - ');
  return line || EMPTY;
}

export type RenewalPersonalFields = { name: string; mobile_phone: string; email: string };

type Errors<T> = Partial<Record<keyof T, string>>;

/** Validação do card de dados pessoais: erro no campo, sem chamar a API. */
export function validateRenewalPersonalData(values: RenewalPersonalFields): Errors<RenewalPersonalFields> {
  const errors: Errors<RenewalPersonalFields> = {};
  if (values.name.trim().split(/\s+/).filter(Boolean).length < 2) {
    errors.name = 'Informe seu nome completo.';
  }
  if (!/^\d{2}9\d{8}$/.test(digitsOf(values.mobile_phone))) {
    errors.mobile_phone = 'Informe um celular com DDD.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Informe um e-mail válido.';
  }
  return errors;
}

/** Validação do card de endereço; o complemento é opcional. */
export const validateRenewalAddress = validateBrazilAddress;

export type RenewalReviewSnapshot = {
  personal: RenewalPersonalFields & { birthdate: string; document: string };
  address: RenewalAddressFields;
  equipment: {
    equipment_type?: string;
    manufacturer?: string;
    model?: string;
    year_of_manufacture?: number | string;
    equipment_value?: number;
  };
};

/** Dados dos cards da revisão: pessoais do contrato; endereço e equipamento da cotação (o contrato é o reserva). */
export function buildRenewalReviewData(contract: RenewalContract, quote: RenewalQuote | null): RenewalReviewSnapshot {
  const insured = contract.insured ?? ({} as RenewalContract['insured']);
  const address = (quote?.address ?? insured.address ?? {}) as Partial<RenewalAddressFields>;
  const quoteItem = quote?.insured_item?.[0];
  const contractItem = contract.insured_item;

  return {
    personal: {
      name: insured.name ?? '',
      birthdate: insured.birthdate ?? '',
      document: insured.document ?? '',
      mobile_phone: insured.mobile_phone ?? '',
      email: insured.email ?? ''
    },
    address: {
      street: address.street ?? '',
      number: address.number ?? '',
      additional_details: address.additional_details ?? '',
      zipcode: address.zipcode ?? '',
      neighborhood: address.neighborhood ?? '',
      city: address.city ?? '',
      state: address.state ?? ''
    },
    equipment: quoteItem
      ? {
          equipment_type: quoteItem.equipment_type,
          manufacturer: quoteItem.manufacturer,
          model: quoteItem.model,
          year_of_manufacture: quoteItem.year_of_manufacture,
          equipment_value: quoteItem.equipment_value
        }
      : {
          equipment_type: contractItem?.equipment_type ?? contractItem?.type_insured_item,
          manufacturer: contractItem?.manufacturer,
          model: contractItem?.model,
          year_of_manufacture: contractItem?.year_of_manufacture,
          equipment_value: contractItem?.insured_amount
        }
  };
}
